import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Plus, Minus, Rocket, Sprout, HandHeart, PartyPopper, ArrowRight } from "lucide-react";
import { IDEAL_SECTORS, IDEAL_PANELS } from "../data/content";
import { Eyebrow, Reveal, scrollToId } from "./shared";
import { OrbitRing, PlanetDot } from "./motifs";

const ICONS = { aerospace: Rocket, sustainability: Sprout, philanthropic: HandHeart, culture: PartyPopper };

export default function IdealFit() {
  const [open, setOpen] = useState(0);
  return (
    <section id="ideal-fit" className="relative py-24 sm:py-32 bg-ivory overflow-hidden">
      <div className="pointer-events-none absolute inset-0 hairline-grid opacity-30" aria-hidden />

      <div className="relative mx-auto max-w-7xl px-5 sm:px-8">
        <Reveal>
          <Eyebrow n={3} className="mb-3">Ideal Fit</Eyebrow>
        </Reveal>
        <Reveal>
          <p className="max-w-2xl text-caramel/80 text-base sm:text-lg leading-relaxed mb-10">
            Environments where I come alive. These are the sectors and cultures that energize me most. If your organization lives in this space, or adjacent to it, I'd love to connect.
          </p>
        </Reveal>

        {/* manifesto banner — orbit ring + warm dark */}
        <Reveal>
          <div className="relative rounded-[1.6rem] bg-warmdark text-ivory p-10 sm:p-16 overflow-hidden observatory-frame">
            <div
              className="absolute -right-16 -top-16 w-72 h-72 rounded-full blur-3xl"
              style={{ background: "radial-gradient(circle, rgba(245,215,126,0.25), transparent 70%)" }}
              aria-hidden
            />
            {/* orbit rings, right side */}
            <div className="pointer-events-none absolute -right-40 -bottom-40 opacity-60" aria-hidden>
              <div className="orbit-slow">
                <OrbitRing size={520} strokeOpacity={0.45} ticks />
              </div>
            </div>

            <div className="relative">
              <div className="font-mono-tech text-xs tracking-[0.28em] uppercase text-honey flex items-center gap-3">
                <span className="w-6 h-px bg-honey/50" />
                What lights me up
              </div>
              <p className="mt-5 font-display font-light text-3xl sm:text-5xl leading-tight max-w-4xl">
                I've spent my career making visionary people more effective. The environments where I come most alive are ones building something that matters; pushing the boundaries of what's possible, protecting the planet we share, lifting people up, or reimagining what a great workplace actually feels like. I bring that same energy, ownership, and care to every organization I support. These are simply the ones where it burns brightest.
              </p>
              <div className="mt-6 font-mono-tech text-[10px] tracking-[0.28em] uppercase text-honey/70">
                Tech · Aerospace · Sustainability · Impact
              </div>
            </div>
          </div>
        </Reveal>

        {/* sector cards — grid borders (exposed skeleton) */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 mt-10" data-testid="sector-cards">
          {IDEAL_SECTORS.map((s, i) => {
            const Icon = ICONS[s.key];
            return (
              <Reveal key={s.key} delay={i * 0.08}>
                <div
                  className="group relative h-full rounded-2xl overflow-hidden border border-border bg-ivory hover:-translate-y-1.5 hover:border-brass/60 transition-all duration-300 shadow-[0_8px_30px_rgba(74,51,34,0.05)] hover:shadow-[0_20px_50px_rgba(74,51,34,0.12)] observatory-frame"
                  data-testid={`sector-${s.key}`}
                >
                  <div className="relative h-40 overflow-hidden">
                    <img
                      src={s.image}
                      alt={s.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      loading="lazy"
                    />
                    <span className="absolute top-3 left-3 w-9 h-9 grid place-items-center rounded-full bg-ivory/90 text-terracotta border border-brass/30">
                      <Icon size={17} />
                    </span>
                    <div className="absolute bottom-2 right-3 font-mono-tech text-[9px] tracking-[0.32em] uppercase text-ivory/90 bg-warmdark/60 rounded-full px-2 py-1">
                      Sector · {String(i + 1).padStart(2, "0")}
                    </div>
                  </div>
                  <div className="p-5">
                    <h3 className="font-display text-2xl text-caramel leading-tight">{s.title}</h3>
                    <p className="mt-2 text-sm text-caramel/75 leading-relaxed">{s.why}</p>
                  </div>
                </div>
              </Reveal>
            );
          })}
        </div>

        {/* expandable panels */}
        <div className="mt-14 grid lg:grid-cols-2 gap-x-10 gap-y-3" data-testid="ideal-panels">
          {IDEAL_PANELS.map((p, i) => (
            <div key={p.title} className="border-b border-brass/20">
              <button
                onClick={() => setOpen(open === i ? -1 : i)}
                data-testid={`ideal-panel-${i}`}
                className="w-full flex items-center justify-between gap-4 py-5 text-left group"
              >
                <span className="flex items-center gap-4">
                  <span className="font-mono-tech text-[10px] tracking-[0.24em] text-brass">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <span className="font-display text-xl sm:text-2xl text-caramel group-hover:text-terracotta transition-colors">
                    {p.title}
                  </span>
                </span>
                <span className="shrink-0 text-terracotta">
                  {open === i ? <Minus size={20} /> : <Plus size={20} />}
                </span>
              </button>
              <AnimatePresence>
                {open === i && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    className="overflow-hidden"
                  >
                    {p.items ? (
                      <ul className="pb-6 pl-8 space-y-4 max-w-3xl">
                        {p.items.map((it) => (
                          <li key={it.label} className="flex gap-4">
                            <span className="text-2xl leading-none shrink-0" aria-hidden="true">{it.icon}</span>
                            <div>
                              <div className="font-mono-tech text-[11px] tracking-[0.24em] uppercase text-terracotta mb-1">{it.label}</div>
                              <p className="text-caramel/80 leading-relaxed">{it.body}</p>
                            </div>
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <p className="pb-6 pl-8 text-caramel/80 leading-relaxed max-w-xl">{p.body}</p>
                    )}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          ))}
        </div>

        {/* CTA bar */}
        <Reveal>
          <div className="mt-14 rounded-[1.4rem] bg-softgold p-8 sm:p-10 flex flex-col sm:flex-row items-center justify-between gap-6 relative overflow-hidden observatory-frame">
            <div className="pointer-events-none absolute -right-20 -top-20 opacity-40" aria-hidden>
              <div className="orbit-rev">
                <OrbitRing size={260} strokeOpacity={0.5} />
              </div>
            </div>
            <div className="relative">
              <div className="flex items-center gap-3 mb-1">
                <PlanetDot />
                <span className="font-mono-tech text-[10px] tracking-[0.28em] uppercase text-warmdark/80">Aspirational · Adjacent · Always</span>
              </div>
              <div className="font-display text-3xl sm:text-4xl text-warmdark font-light">
                Sound like the perfect match?
              </div>
              <div className="text-warmdark/75 mt-1">
                Or even adjacent to one? I would genuinely love to hear from you.
              </div>
            </div>
            <button
              onClick={() => scrollToId("contact")}
              data-testid="ideal-cta"
              data-cosmic="true"
              className="group inline-flex items-center gap-2 rounded-full bg-warmdark text-ivory px-7 py-3.5 font-medium hover:bg-caramel transition-colors shrink-0 relative"
            >
              Let's talk <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
            </button>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
